/**
 * Page anatomy of the back-office boards (Admin-Dashboard, AF-Vue, FS-Vue…): optional back link, purple kicker
 * (11/800 uppercase), h1 26/800, subtitle 13.5 #666A7B (max ~760), actions on the right that wrap below on
 * narrow screens. Plus the shared full-page states: skeleton, 403, 404, route error.
 */
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Lock, SearchX } from "lucide-react";
import { READ_ONLY_BANNER } from "@/contracts/permissions";
import { cn } from "@/lib/cn";
import { Button, ButtonLink } from "./button";
import { Skeleton } from "./states";
import { isStaleChunkError, reloadOnStaleChunk } from "@/lib/stale-chunk";

export function PageHeader({
  kicker,
  title,
  subtitle,
  back,
  actions,
  className,
}: {
  kicker?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  back?: { to: string; label: string };
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-4", className)}>
      <div className="min-w-0 max-w-[800px] flex-[1_1_420px]">
        {back && (
          <Link to={back.to} className="mb-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-muted hover:text-ink">
            <ArrowLeft className="size-3.5" strokeWidth={2.4} />
            {back.label}
          </Link>
        )}
        {kicker && <div className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-primary-500">{kicker}</div>}
        <h1 className="mt-1.5 break-words text-[22px] font-extrabold leading-tight tracking-[-0.02em] text-ink sm:text-[26px]">{title}</h1>
        {subtitle && <p className="mt-1.5 text-[13.5px] leading-[1.55] text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex min-w-0 flex-wrap items-center gap-2.5">{actions}</div>}
    </header>
  );
}

/** Permanent, non-closable (AG-Lecteur, BO drawer for LECTEUR): #EEF0FE / #DCE0FB / #3E38B0. */
export function ReadOnlyBanner({ children = READ_ONLY_BANNER, className }: { children?: ReactNode; className?: string }) {
  return (
    <div role="status" className={cn("mb-5 flex items-center gap-2.5 rounded-xl border border-[#DCE0FB] bg-primary-50 px-3.5 py-2.5 text-[12.5px] font-semibold text-[#3E38B0]", className)}>
      <Lock className="size-3.5 shrink-0" strokeWidth={2.4} />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

/** Content-area skeleton: the shell stays, only the page shimmers (never a full-screen spinner). */
export function PageSkeleton() {
  return (
    <div role="status" aria-label="Chargement…" className="flex flex-col gap-5">
      <div className="space-y-2.5">
        <Skeleton className="h-2.5 w-24" />
        <Skeleton className="h-6 w-72 max-w-full" />
        <Skeleton className="h-3 w-[28rem] max-w-full" />
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 rounded-[18px]" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-[18px]" />
    </div>
  );
}

function CenteredState({ icon, title, children, action }: { icon: ReactNode; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mx-auto mt-6 w-full max-w-[560px] rounded-[18px] border border-line bg-white px-6 py-9 text-center sm:px-10">
      <span className="mx-auto mb-4 inline-flex size-11 items-center justify-center rounded-xl bg-neutral text-ink-2">{icon}</span>
      <h1 className="text-[17px] font-extrabold text-ink">{title}</h1>
      <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed text-muted">{children}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

/** 403 inside the shell (FS-Vue « Accès refusé » card, 560 wide). */
export function ForbiddenPage() {
  return (
    <CenteredState
      icon={<Lock className="size-5" />}
      title="Accès refusé"
      action={
        <ButtonLink to="/" variant="outline" size="md">
          Revenir à mon espace
        </ButtonLink>
      }
    >
      Droits insuffisants pour cette action. Votre rôle ne donne pas accès à cet écran ; demandez à un administrateur si vous en avez besoin.
    </CenteredState>
  );
}

export function NotFoundPage() {
  return (
    <div className="min-h-dvh bg-canvas px-4 py-10">
      <CenteredState
        icon={<SearchX className="size-5" />}
        title="Page introuvable"
        action={
          <ButtonLink to="/" variant="outline" size="md">
            Revenir à mon espace
          </ButtonLink>
        }
      >
        Cette adresse ne correspond à aucun écran de la console Melrys.
      </CenteredState>
    </div>
  );
}

/** 404 inside the shell (unknown path under a signed-in session). */
export function ShellNotFound() {
  return (
    <CenteredState
      icon={<SearchX className="size-5" />}
      title="Page introuvable"
      action={
        <ButtonLink to="/" variant="outline" size="md">
          Revenir à mon espace
        </ButtonLink>
      }
    >
      Cette adresse ne correspond à aucun écran de la console Melrys.
    </CenteredState>
  );
}

export function RouteErrorPage({ error, reset }: { error: unknown; reset: () => void }) {
  // Screen chunk from a previous deploy: reload onto the new version instead of showing the error.
  if (isStaleChunkError(error) && reloadOnStaleChunk()) return null;
  return (
    <CenteredState
      icon={<ArrowLeft className="size-5 rotate-[-45deg]" />}
      title="Chargement impossible"
      action={
        <Button variant="outline" size="md" onClick={reset}>
          Réessayer
        </Button>
      }
    >
      {error instanceof Error ? error.message : "Une erreur est survenue."} Les données déjà affichées ne sont pas perdues.
    </CenteredState>
  );
}
